import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { Activity, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const PAGE_SIZE = 40;

const ACTION_TONE = {
  "tenant.set_status": "bg-violet-100 text-violet-700",
  "tenant.extend_trial": "bg-emerald-100 text-emerald-700",
  "user.update": "bg-sky-100 text-sky-700",
  "announcement.create": "bg-fuchsia-100 text-fuchsia-700",
  "announcement.update": "bg-fuchsia-100 text-fuchsia-700",
  "announcement.delete": "bg-red-100 text-red-700",
  "digest.run_all": "bg-amber-100 text-amber-700",
  "digest.run_tenant": "bg-amber-100 text-amber-700",
};

const ACTIONS = [
  "all",
  "tenant.set_status",
  "tenant.extend_trial",
  "user.update",
  "announcement.create",
  "announcement.update",
  "announcement.delete",
  "digest.run_all",
  "digest.run_tenant",
];

export default function PlatformAuditLog() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action !== "all") params.set("action", action);
      params.set("limit", String(PAGE_SIZE));
      params.set("skip", String(page * PAGE_SIZE));
      const { data } = await api.get(`/platform/audit-log?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [action, page]);

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-5" data-testid="platform-audit">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">Serpix Admin</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("platform.nav.audit")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {t("platform.audit.subtitle", { count: total })}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={action} onValueChange={(v) => { setPage(0); setAction(v); }}>
          <SelectTrigger className="w-56" data-testid="platform-audit-action-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTIONS.map((a) => (
              <SelectItem key={a} value={a}>
                {a === "all" ? t("platform.audit.all_actions") : a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        {loading && (
          <div className="px-6 py-8 text-center text-sm text-zinc-500">
            {t("common.loading")}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-zinc-400">
            {t("platform.audit.empty")}
          </div>
        )}
        <ol className="divide-y divide-zinc-100">
          {items.map((a) => (
            <li
              key={a.id}
              data-testid={`platform-audit-row-${a.id}`}
              className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <div className="flex items-center gap-3 sm:w-40">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-500">
                  <Activity className="h-3.5 w-3.5" />
                </div>
                <div className="text-[11px] text-zinc-500">
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={[
                      "inline-flex rounded-md px-2 py-0.5 font-mono text-[11px] font-semibold",
                      ACTION_TONE[a.action] || "bg-zinc-100 text-zinc-700",
                    ].join(" ")}
                  >
                    {a.action}
                  </span>
                  <span className="text-xs text-zinc-500">
                    by <span className="font-medium text-zinc-800">{a.actor_email}</span>
                  </span>
                  {a.target_type && (
                    <span className="text-[11px] text-zinc-500">
                      on <span className="font-mono">{a.target_type}</span>
                      {a.target_id && (
                        <> · <code className="text-[10px]">{a.target_id.slice(0, 8)}</code></>
                      )}
                    </span>
                  )}
                </div>
                {a.meta && Object.keys(a.meta).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-zinc-500">
                      meta
                    </summary>
                    <pre className="mt-1 max-h-40 overflow-auto rounded bg-zinc-50 p-2 font-mono text-[10px] text-zinc-700">
                      {JSON.stringify(a.meta, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-500" data-testid="platform-audit-pagination">
        <div>
          {t("platform.users.showing", {
            from: page * PAGE_SIZE + 1,
            to: Math.min(total, (page + 1) * PAGE_SIZE),
            total,
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <span className="text-xs">{page + 1} / {maxPage + 1}</span>
          <Button size="sm" variant="ghost" onClick={() => setPage(Math.min(maxPage, page + 1))} disabled={page >= maxPage}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
